import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicSEO from '../../components/public/PublicSEO';
import MarketplaceSearchForm from '../../components/public/MarketplaceSearchForm';
import { DoctorAvatar, Heading, PageContainer, SlotButton } from '../../components/ui';
import api from '../../services/api';
import { getBrancaSlug, getMedicoSlug, formatSlotTime } from '../../utils/slug';
import { getSnapApiData } from '../../utils/snapStore';
import { pickHomeDisciplineList } from '../../utils/publicBranche';
import { publicPath } from '../../utils/canonicalUrl';
import { buildFAQ } from '../../utils/jsonLd';
import { formatPriceFrom } from '../../utils/format';
import './HomePage.css';

const FAQ = [
  { q: 'Come posso prenotare una visita su Virtual Clinic?', a: 'Cerca la prestazione in Visite ed esami, scegli medico e orario, poi conferma dopo accesso o registrazione.' },
  { q: 'Posso spostare un appuntamento?', a: 'Sì, dall’area paziente (Oggi o Appuntamenti) puoi riprogrammare entro le regole di preavviso della struttura.' },
  { q: 'Posso pagare con fondo o assicurazione?', a: 'Al momento la ricerca mostra i prezzi privati. Fondi e assicurazioni saranno disponibili quando i tariffari per compagnia saranno collegati; intanto puoi prenotare privatamente.' },
  { q: 'Come funziona la videochiamata?', a: 'Il giorno della visita, dall’area Oggi apri la stanza video dalla piattaforma. Puoi provare audio e video in anticipo.' },
];

const STEPS = [
  { n: '1', title: 'Cerca', body: 'Prestazione, zona e modo di pagamento.' },
  { n: '2', title: 'Scegli', body: 'Medico, giorno e orario libero (in verde).' },
  { n: '3', title: 'Conferma', body: 'Prenoti e ti prepari dall’area paziente.' },
];

const getJsonLd = () => [buildFAQ(FAQ)];

/**
 * Home marketplace (10_RESTYLING §5.1 + DESIGN.md): calma, orari veri, un solo CTA bordeaux.
 */
const HomePage = () => {
  const snapBranche = getSnapApiData('/public/branche');
  const [discipline, setDiscipline] = useState(() => pickHomeDisciplineList(snapBranche));
  const [orariLiberi, setOrariLiberi] = useState([]);

  useEffect(() => {
    api.get('/public/branche')
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          setDiscipline(pickHomeDisciplineList(res.data));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/public/search', { params: { sort: 'disponibilita', raggioKm: 200 } })
      .then((res) => {
        const hits = Array.isArray(res.data?.results) ? res.data.results : [];
        setOrariLiberi(
          hits
            .filter((h) => (h.prossimiSlot || []).length > 0)
            .slice(0, 4)
        );
      })
      .catch(() => setOrariLiberi([]));
  }, []);

  return (
    <div className="vc-home vc-home--marketplace">
      <PublicSEO
        title="Prenota una visita, in studio o in video"
        description="Cerca disponibilità di visite in studio o in video, confronta prezzi e orari, prenota su Virtual Clinic."
        canonicalPath="/"
        keywords="prenota visita, disponibilità medici, telemedicina, fondi sanitari, Virtual Clinic"
        ogImageAlt="Virtual Clinic - Prenota visite ed esami"
        jsonLd={getJsonLd()}
      />

      <section className="vc-mkt-hero">
        <PageContainer className="vc-mkt-hero__grid">
          <div className="vc-mkt-hero__copy">
            <Heading level={1} className="vc-mkt-hero__title">
              Prenota una visita, in studio o in video
            </Heading>
            <p className="vc-mkt-hero__lead">
              Confronti medici, prezzi e orari aggiornati. In pochi passi sei prenotato.
            </p>
            <div className="vc-mkt-hero__form">
              <MarketplaceSearchForm />
            </div>
            <ul className="vc-mkt-trust" aria-label="Perché Virtual Clinic">
              <li>Medici iscritti all&apos;Albo</li>
              <li>Pagamento sicuro</li>
              <li>Sposti o annulli gratis fino a 24 ore prima</li>
            </ul>
          </div>

          <aside className="vc-mkt-now" aria-label="Orari liberi adesso">
            <div className="vc-mkt-now__head">
              <p className="vc-mkt-now__kicker">Orari liberi adesso</p>
              <Link to={publicPath('/ricerca')} className="vc-mkt-now__all">
                Vedi tutti
              </Link>
            </div>
            {orariLiberi.length === 0 ? (
              <p className="vc-mkt-now__empty">
                <Link to={publicPath('/ricerca')}>Cerca disponibilità</Link>
                {' '}per vedere gli orari aggiornati.
              </p>
            ) : (
              <ul className="vc-mkt-now__list">
                {orariLiberi.map((hit) => {
                  const slug = hit.slug || getMedicoSlug({
                    id: hit.idMedico,
                    nome: hit.medicoNome,
                    cognome: hit.medicoCognome,
                  });
                  const name = `${hit.medicoTitolo || 'Dott.'} ${hit.medicoNome || ''} ${hit.medicoCognome || ''}`.trim();
                  const slot = (hit.prossimiSlot || [])[0];
                  const branca = hit.brancaNome || hit.branca || '';
                  return (
                    <li key={hit.idMedico} className="vc-mkt-now__row">
                      <DoctorAvatar
                        src={hit.urlImg}
                        nome={hit.medicoNome}
                        cognome={hit.medicoCognome}
                        size={48}
                      />
                      <div className="vc-mkt-now__meta">
                        <Link to={publicPath(`/medici/${slug}`)} className="vc-mkt-now__name">
                          {name}
                        </Link>
                        <span className="vc-mkt-now__sub">
                          {[branca, hit.prezzo != null ? formatPriceFrom(hit.prezzo) : '']
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </div>
                      {slot && (
                        <SlotButton
                          time={formatSlotTime(slot.oraInizio) || slot.label}
                          onClick={() => {
                            window.location.href = publicPath(`/medici/${slug}`);
                          }}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>
        </PageContainer>
      </section>

      <section className="vc-section vc-mkt-steps">
        <PageContainer>
          <Heading level={2}>Come funziona</Heading>
          <p className="vc-section__sub">Tre passi, senza sorprese sul prezzo.</p>
          <ol className="vc-mkt-steps__grid">
            {STEPS.map((s) => (
              <li key={s.n} className="vc-mkt-steps__item">
                <span className="vc-mkt-steps__num" aria-hidden="true">{s.n}</span>
                <div>
                  <strong className="vc-mkt-steps__title">{s.title}</strong>
                  <p className="vc-mkt-steps__body">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
          <Link to={publicPath('/come-funziona')} className="vc-btn vc-btn-outline">
            Scopri di più
          </Link>
        </PageContainer>
      </section>

      <section className="vc-section vc-specializzazioni">
        <PageContainer>
          <Heading level={2}>Discipline</Heading>
          <p className="vc-section__sub">Solo aree cliniche con medici disponibili.</p>
          <div className="vc-spec-grid">
            {discipline.map((d) => (
              <Link
                to={publicPath(`/discipline/${getBrancaSlug(d)}`)}
                key={d.id}
                className="vc-spec-card"
              >
                <span className="vc-spec-name">{d.branca}</span>
                {d.intro && <span className="vc-spec-desc">{d.intro}</span>}
              </Link>
            ))}
          </div>
          <div className="vc-section-cta">
            <Link to={publicPath('/discipline')} className="vc-btn vc-btn-outline vc-btn-cta">
              Tutte le discipline
            </Link>
            <Link to={publicPath('/ricerca')} className="vc-btn vc-btn-primary vc-btn-cta">
              Cerca disponibilità
            </Link>
          </div>
        </PageContainer>
      </section>

      <section className="vc-section vc-mkt-fondi">
        <PageContainer className="vc-mkt-fondi__inner">
          <div className="vc-mkt-fondi__copy">
            <Heading level={2}>Fondi e assicurazioni</Heading>
            <p className="vc-section__sub">
              In arrivo: prezzi convenzionati per fondo o polizza, quando i tariffari per compagnia saranno collegati. Oggi in ricerca trovi i prezzi privati.
            </p>
          </div>
          <Link to={publicPath('/fondi-sanitari')} className="vc-btn vc-btn-outline">
            Come funzionerà
          </Link>
        </PageContainer>
      </section>

      <section className="vc-section vc-faq">
        <PageContainer narrow>
          <Heading level={2}>Domande frequenti</Heading>
          <div className="vc-faq-list">
            {FAQ.map((item) => (
              <details key={item.q} className="vc-faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </PageContainer>
      </section>

      <section className="vc-mkt-b2b">
        <PageContainer className="vc-mkt-b2b__inner">
          <Heading level={2}>Sei una struttura?</Heading>
          <p>Pubblica l&apos;agenda, inserisci il widget Prenota e collega il gestionale.</p>
          <Link to={publicPath('/per-le-strutture')} className="vc-btn vc-btn-primary">
            Per le strutture
          </Link>
        </PageContainer>
      </section>
    </div>
  );
};

export default HomePage;
